/* External */
import { connect } from 'react-redux';

/* Ott-common */
import { isWithinIntervalDate } from '~/utils/dateUtils';

/* Components */
import SportSection from '../SportSection';
import FeaturedCarouselCard from '../FeaturedCarouselCard';
import CardStationWithSchedule from '../CardStationWithSchedule';
import CollectionCard from '../CollectionCard';
import CardContinueWatching from '../CardContinueWatching';
import ImageBanner from '../ImageBanner';
import LiveCard from '../LiveCard';
import CardVod from '../CardVod';
import SeeAllCardVod from '../SeeAllCardVod';
import SeeAllCardLive from '../SeeAllCardLive';
import GroupStanding from '../GroupStanding';
import PlayOffs from '../PlayOffs';

/* Utils */
import {
  createRedirectFromCollection,
  redirectTo,
  redirectToDetail,
  redirectToPlayer,
} from '../../utils/history';
import { SOURCE_CAROUSEL_ID, PageType, PROVIDERS } from '../../utils/constants';
import { getPlaySourcePage, PLAY_SOURCE_SECTION } from '../../utils/trackSourceConviva';
import { isShow, isSchedule } from '../../utils/metaDataUtils';
import { getLink, Routes } from '../../utils/router';
import { CardType } from '../../utils/cardsUtils';
import { selectPolymorphicEntityById } from '../../storage/selectors';
import { COMPONENT_TYPES, GTM_EVENT_NAMES } from '../../utils/googleTags';

/* Storage */
import store from '../../storage/configureStore';
import { selectScheduleById } from '../../storage/schedules';
import { selectContentById } from '../../storage/contents';
import { selectShowById } from '../../storage/shows';

const withItem = connect((state, { item }) => ({ item: selectPolymorphicEntityById(item)(state) }));
const selectContents = ({ contents }) => contents;
const selectCollections = ({ collections }) => collections;

// Função utilitária para usar fora de componentes
export function getContentInfo(contentId) {
  if (!contentId) {
    return undefined;
  }

  const state = store.getState();

  const contentSelector = selectContentById(contentId);
  const content = contentSelector(state);

  if (content) {
    return content;
  }

  const showSelector = selectShowById(contentId);
  return showSelector(state);
}

function getCurrentScheduleIdFromChannel(channel, now = Date.now()) {
  const schedulesMap = channel?.schedules;

  if (!schedulesMap || schedulesMap.size === 0) return undefined;

  let currentScheduleId;
  let currentStart = Number.NEGATIVE_INFINITY;

  for (const [key, scheduleId] of schedulesMap.entries()) {
    const [startTime, endTime] = key.split('_').map(Number);

    if (Number.isNaN(startTime) || Number.isNaN(endTime)) {
      continue;
    }

    if (now >= startTime && now <= endTime && startTime > currentStart) {
      currentStart = startTime;
      currentScheduleId = scheduleId;
    }
  }

  return currentScheduleId;
}

/**
 * Use the station itself (Channel already coming from Redux via withItem) to
 * find the current schedule and the associated content, without rebuscar the channel in the store.
 *
 * @param {Object} station - normalized Channel entity (carousel item)
 * @param {number} [now=Date.now()]
 * @returns {{ channel, schedule, content }}
 */
export function getChannelScheduleAndContentByStation(station, now = Date.now()) {
  if (!station) return { channel: undefined, schedule: undefined, content: undefined };

  const state = store.getState();

  const channel = station;

  const scheduleRawId = getCurrentScheduleIdFromChannel(channel, now);
  if (!scheduleRawId) return { channel, schedule: undefined, content: undefined };

  const scheduleId = `${scheduleRawId}-${channel?.sourceProvider ?? PROVIDERS.VRIO}`;

  const scheduleSelector = selectScheduleById(scheduleId);
  const schedule = scheduleSelector(state);
  if (!schedule) return { channel, schedule: undefined, content: undefined };

  const contentId = schedule.content;
  const content = contentId ? getContentInfo(contentId) : undefined;

  return { channel, schedule, content };
}

/**
 * Handles click learn action for tracking purposes
 * @param {Object} params
 * @param {Object} params.contentInfo - Contain streaming and asset data
 * @param {string} [params.msoProvider='']
 * @param {string} [params.userType='']
 * @param {string} [params.playSource=''] - Play source identifier for tracking
 * @param {Function} params.fetchClickAction - Function to send click learn action data
 */
const handleClickLearnAction = ({
  contentInfo,
  msoProvider = '',
  userType = '',
  playSource = '',
  fetchClickAction,
}) => {
  if (!contentInfo) {
    console.warn('handleClickLearnAction: contentInfo is required');
    return;
  }

  if (!fetchClickAction || typeof fetchClickAction !== 'function') {
    console.warn('handleClickLearnAction: fetchClickAction must be a function');
    return;
  }

  const clickActionParams = {
    actionOrigin: contentInfo.actionOrigin || '',
    userOrigin: {
      playSource,
      userLogin: userType,
      msoProvider,
    },
    contentType: contentInfo.isLive ? 1 : 2,
    guideId: contentInfo?.streaming?.id || contentInfo?.vrioAssetId || '',
    vrioAssetId: contentInfo.vrioAssetId,
  };

  fetchClickAction(clickActionParams);
};

export const ComponentByCardType = {
  [CardType.FEATURED]: {
    CardComponent: withItem(FeaturedCarouselCard),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      pageId,
      handleClickSection,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => ({ item }) => {
      const contentInfo = getContentInfo(
        isSchedule(item) ? item.content : undefined,
      ) || item;

      const state = {
        ...locationState({ content: item, componentType: CardType.FEATURED }),
        actionOrigin: contentInfo?.actionOrigin || '',
      };

      handleClickSection(true);
      carouselCommonTagEvent({
        eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
        content: { ...item.toJS(), ...contentInfo.toJS() },
        extraData: state,
      });

      handleClickLearnAction({
        contentInfo,
        msoProvider,
        userType,
        playSource,
        fetchClickAction,
      });
      if (isShow(item)) {
        redirectToDetail(item, { state });
      }
      redirectToPlayer(item, {
        page: getPlaySourcePage(pageId),
        section: PLAY_SOURCE_SECTION.FEATURED_CAROUSEL,
        state,
      });
    },
  },
  [CardType.CHANNEL]: {
    CardComponent: withItem(CardStationWithSchedule),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      pageId,
      carousel,
      handleClickSection,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => (
      ({ item: station }) => {
        const { content } = getChannelScheduleAndContentByStation(station);

        const clickActionParams = {
          actionOrigin: station?.actionOrigin || content?.actionOrigin || '',
          userOrigin: {
            playSource,
            userLogin: userType || '',
            msoProvider: msoProvider || '',
          },
          contentType: 1,
          guideId: content?.guideId || content?.vrioAssetId || '',
          vrioAssetId: content?.vrioAssetId || '',
        };

        if (content && typeof fetchClickAction === 'function') {
          fetchClickAction(clickActionParams);
        }

        const state = {
          ...locationState({
            content: station,
            componentType: COMPONENT_TYPES.RECENTLY_WATCHED,
            sectionName: COMPONENT_TYPES.RECENTLY_WATCHED,
          }),
          actionOrigin: station?.actionOrigin || '',
        };

        handleClickSection();
        carouselCommonTagEvent({
          eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
          content: station,
          extraData: state,
        });

        redirectToPlayer(station, {
          page: getPlaySourcePage(pageId),
          section: carousel?.sourceCarouselId === SOURCE_CAROUSEL_ID.RECENTLY_WATCHED
            ? PLAY_SOURCE_SECTION.RECENTLY_WATCHED_CAROUSEL
            : carousel?.title,
          state,
        });
      }
    ),
  },
  [CardType.SPORT_SECTION]: {
    CardComponent: SportSection,
    itemsSelector: selectCollections,
    CarouselLengthByCardType: 32,
    handleEnterPressCreator: ({
      handleClickSection,
      carousel,
      pageId,
      carouselCommonTagEvent,
    }) => (
      createRedirectFromCollection({
        beforeRedirect: handleClickSection,
        carousel,
        pageId,
        carouselCommonTagEvent,
      })
    ),
  },
  [CardType.IMAGE_BANNER]: {
    CardComponent: ImageBanner,
    itemsSelector: selectCollections,
    CarouselLengthByCardType: 1,
    handleEnterPressCreator: ({
      handleClickSection,
      carousel,
      pageId,
      carouselCommonTagEvent,
    }) => (
      createRedirectFromCollection({
        beforeRedirect: handleClickSection,
        carousel,
        pageId,
        carouselCommonTagEvent,
      })
    ),
  },
  [CardType.COLLECTION]: {
    CardComponent: CollectionCard,
    itemsSelector: selectCollections,
    handleEnterPressCreator: ({
      handleClickSection,
      carousel,
      pageId,
      carouselCommonTagEvent,
    }) => (
      createRedirectFromCollection({
        beforeRedirect: handleClickSection,
        carousel,
        pageId,
        carouselCommonTagEvent,
      })
    ),
  },
  [CardType.CONTINUE_WATCHING]: {
    CardComponent: withItem(CardContinueWatching),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      pageId,
      handleClickSection,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => (
      ({ item: content }) => {
        const contentInfo = getContentInfo(
          isSchedule(content) ? content.content : undefined,
        ) || content;

        const state = {
          ...locationState({
            content,
            componentType: COMPONENT_TYPES.CONTINUE_WATCHING,
            sectionName: COMPONENT_TYPES.CONTINUE_WATCHING,
          }),
          actionOrigin: contentInfo?.actionOrigin || '',
        };

        handleClickSection();
        carouselCommonTagEvent({
          eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
          content,
          extraData: state,
        });

        handleClickLearnAction({
          contentInfo,
          msoProvider,
          userType,
          playSource,
          fetchClickAction,
        });
        redirectToPlayer(content, {
          page: getPlaySourcePage(pageId),
          section: PLAY_SOURCE_SECTION.RESUME_WATCHING_CAROUSEL,
          state,
        });
      }
    ),
  },
  [CardType.LIVE]: {
    CardComponent: withItem(LiveCard),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      pageId,
      handleClickSection,
      carousel,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => (
      ({ item: schedule }) => {
        handleClickSection();
        const startDate = schedule?.startTime
          ? new Date(schedule?.startTime)
          : undefined;
        const endDate = schedule?.endTime ? new Date(schedule?.endTime) : undefined;
        const isLive = isWithinIntervalDate(startDate, endDate);

        const contentInfo = getContentInfo(
          isSchedule(schedule) ? schedule.content : undefined,
        ) || schedule;

        const state = {
          ...locationState({ content: schedule, componentType: CardType.LIVE }),
          actionOrigin: contentInfo?.actionOrigin || '',
        };

        carouselCommonTagEvent({
          eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
          content: schedule,
          extraData: state,
          isLive: true,
        });

        handleClickLearnAction({
          contentInfo,
          msoProvider,
          userType,
          playSource,
          fetchClickAction,
        });

        if (isLive) {
          redirectToPlayer(schedule, {
            page: getPlaySourcePage(pageId),
            section:
              pageId === PageType.SPORTS
                ? PLAY_SOURCE_SECTION.LIVE_NOW_CAROUSEL
                : carousel?.title,
            state,
          });
        } else {
          redirectToDetail(schedule, { state });
        }
      }
    ),
    SeeAllCardComponent: SeeAllCardLive,
    handleSeeAllEnterPressCreator: ({
      carousel,
      handleClickSection,
      carouselCommonTagEvent,
      locationState,
    }) => () => {
      handleClickSection();
      carouselCommonTagEvent({
        eventName: GTM_EVENT_NAMES.CAROUSELS.SEE_MORE,
        extraData: {
          ...locationState({ componentType: CardType.LIVE }),
        },
        isLive: true,
      });
      const detailRoute = Routes.Private.Detail;
      redirectTo(getLink(detailRoute.Section, carousel));
    },
  },
  [CardType.VOD]: {
    CardComponent: withItem(CardVod),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      handleClickSection,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => ({ content }) => {
      handleClickSection();

      const state = {
        ...locationState({ content, componentType: CardType.VOD }),
        actionOrigin: content?.actionOrigin || '',
      };

      carouselCommonTagEvent({
        eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
        content,
        extraData: state,
      });

      handleClickLearnAction({
        contentInfo: content,
        msoProvider,
        userType,
        playSource,
        fetchClickAction,
      });
      redirectToDetail(content, { state });
    },
    SeeAllCardComponent: SeeAllCardVod,
    handleSeeAllEnterPressCreator: ({
      carousel,
      handleClickSection,
      carouselCommonTagEvent,
      locationState,
    }) => () => {
      handleClickSection();
      carouselCommonTagEvent({
        eventName: GTM_EVENT_NAMES.CAROUSELS.SEE_MORE,
        extraData: {
          ...locationState({ componentType: CardType.VOD }),
        },
      });
      if (carousel?.sourceCarouselId === SOURCE_CAROUSEL_ID.MY_LIST) {
        redirectTo(getLink(Routes.Private.MyList));
      } else {
        const detailRoute = Routes.Private.Detail;
        redirectTo(getLink(detailRoute.Section, carousel));
      }
    },
  },
  [CardType.PLAY_OFFS]: {
    CardComponent: PlayOffs,
    itemsSelector: (carousel) => [carousel?.sport?.standing],
    CarouselLengthByCardType: 1,
  },
  [CardType.GROUP_STANDING]: {
    CardComponent: GroupStanding,
    itemsSelector: (carousel) => carousel?.sport?.standing?.groups,
    CarouselLengthByCardType: 8,
  },
};
